import React, { useRef, useCallback, useEffect, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { makeRequest } from "@/makeRequest";
import { MY_ENDPOINTS } from "@raashidh/shared";
import { useMyDialog } from "@/context/MyDialogProvider";

type NewsItem = {
  id: number;
  title: string;
  link: string;
  description: string;
  source: string;
  published: string;
  image: string;
};

export default function MvNews() {
    const { open } = useMyDialog();
  const loaderRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const [autoLoad, setAutoLoad] = useState(true);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["mv-news"],

    queryFn: async ({ pageParam = 1 }) => {
      return await makeRequest<{
        success: boolean;
        data: NewsItem[];
        page: number;
        limit: number;
        hasMore: boolean;
        nextCursor: number | null;
      }>(MY_ENDPOINTS.SCRAPER_ENDPOINTS.GET_MV_NEWS.path, {
        method: "GET",
        query: {
          page: pageParam,   // ✅ IMPORTANT
          limit: 20,
          q: search || undefined, // optional server-side filter
        },
        auth: true,
      });
    },

    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.nextCursor : undefined;
    },

    initialPageParam: 1,
  });

  const items = data?.pages.flatMap((p) => p.data) || [];

  const isLoadingMore =
    isFetchingNextPage || (isFetching && !isLoading);

  // reset when search changes
  useEffect(() => {
    refetch();
  }, [search]);

  // infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];

      if (
        target.isIntersecting &&
        hasNextPage &&
        !isLoadingMore &&
        autoLoad
      ) {
        fetchNextPage();
      }
    },
    [hasNextPage, isLoadingMore, fetchNextPage, autoLoad]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "100px",
      threshold: 0,
    });

    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [handleObserver]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

 const viewDetails = (url: any) => {
  open({
    content: (
      <div className="flex justify-center">
        <iframe
          src={url}
          title="details"
          width="400"
          height="600"
          loading="lazy"
          referrerPolicy="no-referrer"
          sandbox="
            allow-scripts
            allow-forms
            allow-same-origin
          "
          allow="
            accelerometer 'none';
            camera 'none';
            geolocation 'none';
            gyroscope 'none';
            microphone 'none';
            payment 'none';
            usb 'none'
          "
          style={{
            border: "none",
            borderRadius: "8px",
          }}
        />
      </div>
    ),
  });
};

  return (
    <div dir="rtl" className="container mx-auto px-4 py-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row gap-3 justify-between items-center mb-6">

        <div>
          <h1 className="text-3xl font-bold dark:text-white">
            Mv News
          </h1>
        </div>

   
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        {items.map((item) => (
          <div
            key={item.id}
            onClick={() => viewDetails(item.link)}
            //href={item.link}
            className="dhi bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow"
          >
            {item.image && (
              <img
                src={item.image}
                className="w-full h-40 object-cover"
              />
            )}

            <div className="p-4">
            
              <div className="w-full flex justify-between items-center ">
                <div className="text-xs">
                    {item.source}
                </div>
                <div className="text-xs text-gray-500">
                    {item.published.slice(0, 10)}
                    {item.published.slice(0, 10) !== item.published.slice(11, 16) && ` | ${item.published.slice(11, 16)}`}
                </div>
              </div>

              <h2 className="font-bold mt-2">
                {item.title}
              </h2>

              <p className="text-sm text-gray-600 mt-2 line-clamp-3">
                {item.description}
              </p>
            </div>
          </div>
        ))}

      </div>

      {/* Loader */}
      <div ref={loaderRef} className="py-10 text-center">
        {hasNextPage ? (
          isLoadingMore ? (
            <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          ) : (
            <button
              onClick={() => fetchNextPage()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg"
            >
              Load More
            </button>
          )
        ) : (
          <p className="text-gray-500">End of news</p>
        )}
      </div>

    </div>
  );
}